import { writable, derived, get } from 'svelte/store';
import { db, hasCloud, type UserProfile } from './db';
import { newId } from './ids';

export type AuthStatus = 'loading' | 'anonymous' | 'pending' | 'approved' | 'admin';

export interface AuthState {
  status: AuthStatus;
  email: string | null;
  profile: UserProfile | null;
}

export const authState = writable<AuthState>({
  status: 'loading',
  email: null,
  profile: null,
});

async function upsertProfile(email: string, opts: { autoApprove?: boolean } = {}): Promise<UserProfile> {
  const lower = email.toLowerCase();
  let p = await db.profiles.where('email').equals(lower).first();
  if (!p) {
    const isFirst = (await db.profiles.count()) === 0;
    const approved = isFirst || !!opts.autoApprove;
    p = {
      id: newId(),
      email: lower,
      role: isFirst ? 'admin' : 'user',
      approved,
      approvedBy: approved ? (isFirst ? lower : 'invite-link') : undefined,
      approvedAt: approved ? Date.now() : undefined,
      createdAt: Date.now(),
    };
    await db.profiles.add(p);
  } else if (opts.autoApprove && !p.approved) {
    // Existing pending user followed an invite link — promote to approved.
    await db.profiles.update(p.id, {
      approved: true,
      approvedBy: 'invite-link',
      approvedAt: Date.now(),
    });
    p = (await db.profiles.get(p.id))!;
  }
  return p;
}

async function findProfileByEmail(email: string): Promise<UserProfile | undefined> {
  return db.profiles.where('email').equals(email.toLowerCase()).first();
}

function project(p: UserProfile | null, email: string | null): AuthState {
  if (!email || !p) return { status: 'anonymous', email: null, profile: null };
  if (p.role === 'admin') return { status: 'admin', email, profile: p };
  if (!p.approved)        return { status: 'pending', email, profile: p };
  return { status: 'approved', email, profile: p };
}

// Local-dev fallback — stores "signed in" email in localStorage when Dexie
// Cloud isn't configured yet. Lets the app be used/tested end-to-end without
// a cloud URL. Replaced automatically once VITE_DEXIE_CLOUD_URL is set.
const LOCAL_KEY = 'cousins.localEmail';

async function initLocal() {
  const stored = localStorage.getItem(LOCAL_KEY);
  if (!stored) {
    authState.set({ status: 'anonymous', email: null, profile: null });
    return;
  }
  const p = await upsertProfile(stored);
  authState.set(project(p, stored));
}

async function initCloud() {
  // Subscribe to Dexie Cloud user changes.
  db.cloud.currentUser.subscribe(async (user: { email?: string | null } | null) => {
    const email = user?.email ?? null;
    if (!email) {
      authState.set({ status: 'anonymous', email: null, profile: null });
      return;
    }
    const p = await upsertProfile(email);
    authState.set(project(p, email));
  });
}

export async function initAuth(): Promise<void> {
  if (hasCloud) await initCloud();
  else          await initLocal();
}

export async function signIn(email: string, opts: { autoApprove?: boolean } = {}): Promise<void> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
    throw new Error('Enter a valid email address.');
  }
  if (hasCloud) {
    await db.cloud.login({ email: trimmed });
  } else {
    localStorage.setItem(LOCAL_KEY, trimmed);
    const p = await upsertProfile(trimmed, { autoApprove: opts.autoApprove });
    authState.set(project(p, trimmed));
  }
}

export async function signOut(): Promise<void> {
  if (hasCloud) await db.cloud.logout();
  localStorage.removeItem(LOCAL_KEY);
  authState.set({ status: 'anonymous', email: null, profile: null });
}

export async function approveUser(email: string): Promise<void> {
  const actor = get(authState);
  if (actor.status !== 'admin') throw new Error('Not permitted.');
  const p = await findProfileByEmail(email);
  if (!p) throw new Error('Profile not found.');
  await db.profiles.update(p.id, {
    approved: true,
    approvedBy: actor.email ?? undefined,
    approvedAt: Date.now(),
  });
}

export async function rejectUser(email: string): Promise<void> {
  const actor = get(authState);
  if (actor.status !== 'admin') throw new Error('Not permitted.');
  const p = await findProfileByEmail(email);
  if (p) await db.profiles.delete(p.id);
}

export async function setUserRole(email: string, role: 'admin' | 'user'): Promise<void> {
  const actor = get(authState);
  if (actor.status !== 'admin') throw new Error('Not permitted.');
  const p = await findProfileByEmail(email);
  if (!p) throw new Error('Profile not found.');
  await db.profiles.update(p.id, { role });
}

export const isAdmin = derived(authState, ($s) => $s.status === 'admin');
export const isApproved = derived(authState, ($s) => $s.status === 'admin' || $s.status === 'approved');

export { newId };
